export type LegalAttachmentKind = 'agb' | 'widerrufsformular';

export const LEGAL_ATTACHMENT_KINDS: readonly LegalAttachmentKind[] = [
  'agb',
  'widerrufsformular',
] as const;

export type LegalAttachmentSettings = {
  has_agb: boolean;
  agb_filename: string | null;
  agb_attach_private: boolean;
  agb_attach_business: boolean;
  has_widerrufsformular: boolean;
  widerrufsformular_filename: string | null;
  widerrufsformular_attach_private: boolean;
  widerrufsformular_attach_business: boolean;
};

export const DEFAULT_LEGAL_ATTACHMENT_SETTINGS: LegalAttachmentSettings = {
  has_agb: false,
  agb_filename: null,
  agb_attach_private: true,
  agb_attach_business: true,
  has_widerrufsformular: false,
  widerrufsformular_filename: null,
  widerrufsformular_attach_private: true,
  widerrufsformular_attach_business: false,
};

function parseBool(raw: unknown, fallback: boolean): boolean {
  if (typeof raw === 'boolean') return raw;
  if (raw === 1 || raw === '1' || raw === 'true') return true;
  if (raw === 0 || raw === '0' || raw === 'false') return false;
  return fallback;
}

function parseFilename(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  return t || null;
}

export function isLegalAttachmentKind(value: unknown): value is LegalAttachmentKind {
  return value === 'agb' || value === 'widerrufsformular';
}

export function legalAttachmentLabel(kind: LegalAttachmentKind): string {
  return kind === 'agb' ? 'AGB' : 'Widerrufsformular';
}

export function parseExcludedStandardAttachments(raw: unknown): LegalAttachmentKind[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<LegalAttachmentKind>();
  const out: LegalAttachmentKind[] = [];
  for (const item of raw) {
    if (!isLegalAttachmentKind(item) || seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
}

export function legalAttachmentSettingsFromUnknown(raw: unknown): LegalAttachmentSettings {
  const row = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    has_agb: parseBool(row.has_agb, false) || Boolean(row.agb_object_path),
    agb_filename: parseFilename(row.agb_filename),
    agb_attach_private: parseBool(
      row.agb_attach_private,
      DEFAULT_LEGAL_ATTACHMENT_SETTINGS.agb_attach_private,
    ),
    agb_attach_business: parseBool(
      row.agb_attach_business,
      DEFAULT_LEGAL_ATTACHMENT_SETTINGS.agb_attach_business,
    ),
    has_widerrufsformular:
      parseBool(row.has_widerrufsformular, false) || Boolean(row.widerrufsformular_object_path),
    widerrufsformular_filename: parseFilename(row.widerrufsformular_filename),
    widerrufsformular_attach_private: parseBool(
      row.widerrufsformular_attach_private,
      DEFAULT_LEGAL_ATTACHMENT_SETTINGS.widerrufsformular_attach_private,
    ),
    widerrufsformular_attach_business: parseBool(
      row.widerrufsformular_attach_business,
      DEFAULT_LEGAL_ATTACHMENT_SETTINGS.widerrufsformular_attach_business,
    ),
  };
}

export function isAngebotDocumentType(documentTypeLabel: string): boolean {
  return documentTypeLabel.trim() === 'Angebot';
}

function kindHasFile(kind: LegalAttachmentKind, settings: LegalAttachmentSettings): boolean {
  return kind === 'agb' ? settings.has_agb : settings.has_widerrufsformular;
}

function kindMatchesCustomer(
  kind: LegalAttachmentKind,
  settings: LegalAttachmentSettings,
  isPrivateCustomer: boolean,
): boolean {
  if (kind === 'agb') {
    return isPrivateCustomer ? settings.agb_attach_private : settings.agb_attach_business;
  }
  return isPrivateCustomer
    ? settings.widerrufsformular_attach_private
    : settings.widerrufsformular_attach_business;
}

/** File exists and targeting matches this customer — shown in Anhänge even if currently excluded. */
export function isLegalAttachmentApplicable(opts: {
  kind: LegalAttachmentKind;
  documentTypeLabel: string;
  isPrivateCustomer: boolean;
  settings: LegalAttachmentSettings;
}): boolean {
  if (!isAngebotDocumentType(opts.documentTypeLabel)) return false;
  if (!kindHasFile(opts.kind, opts.settings)) return false;
  return kindMatchesCustomer(opts.kind, opts.settings, opts.isPrivateCustomer);
}

/** Applicable and not excluded for this offering — merged into the Angebot PDF. */
export function shouldIncludeLegalAttachment(opts: {
  kind: LegalAttachmentKind;
  documentTypeLabel: string;
  isPrivateCustomer: boolean;
  settings: LegalAttachmentSettings;
  excluded: readonly LegalAttachmentKind[];
}): boolean {
  if (opts.excluded.includes(opts.kind)) return false;
  return isLegalAttachmentApplicable(opts);
}

export function legalAttachmentFilename(
  kind: LegalAttachmentKind,
  settings: LegalAttachmentSettings,
): string {
  const stored =
    kind === 'agb' ? settings.agb_filename : settings.widerrufsformular_filename;
  return stored || `${legalAttachmentLabel(kind)}.pdf`;
}
